import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as LucideIcons from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/contact`);
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const updateSubmission = async (id) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/contact/${id}`, {
        status: status || undefined,
        notes: notes || undefined
      });
      toast.success('Submission updated successfully');
      fetchSubmissions();
      setSelectedSubmission(null);
      setNotes('');
      setStatus('');
    } catch (error) {
      console.error('Error updating submission:', error);
      toast.error('Failed to update submission');
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/contact/${id}`);
      toast.success('Submission deleted successfully');
      fetchSubmissions();
    } catch (error) {
      console.error('Error deleting submission:', error);
      toast.error('Failed to delete submission');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'bg-blue-500';
      case 'contacted':
        return 'bg-yellow-500';
      case 'completed':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1e3a8a] via-[#0F172A] to-[#0a0e1a] flex items-center justify-center">
        <LucideIcons.Loader2 className="h-12 w-12 animate-spin text-[#38BDF8]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1e3a8a] via-[#0F172A] to-[#0a0e1a] py-12 px-6">
      <div className="container mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Admin Dashboard</h1>
            <p className="text-gray-300">Manage contact form submissions</p>
          </div>
          <Button onClick={fetchSubmissions} className="bg-[#2563EB] hover:bg-[#1d4ed8]">
            <LucideIcons.RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-[#1a2332] to-[#0f1419] border-[#38BDF8]/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-300">Total Submissions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{submissions.length}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-[#1a2332] to-[#0f1419] border-[#38BDF8]/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-300">New</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-400">
                {submissions.filter(s => s.status === 'new').length}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-[#1a2332] to-[#0f1419] border-[#38BDF8]/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-300">Contacted</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-yellow-400">
                {submissions.filter(s => s.status === 'contacted').length}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-[#1a2332] to-[#0f1419] border-[#38BDF8]/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-300">Completed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-400">
                {submissions.filter(s => s.status === 'completed').length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Submissions List */}
        <div className="grid gap-6">
          {submissions.map((submission) => (
            <Card key={submission.id} className="bg-gradient-to-br from-[#1a2332] to-[#0f1419] border-[#38BDF8]/20">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <CardTitle className="text-xl text-white">{submission.name}</CardTitle>
                      <Badge className={getStatusColor(submission.status)}>
                        {submission.status}
                      </Badge>
                    </div>
                    <CardDescription className="text-gray-400">
                      {submission.company} • {submission.email} • {submission.phone}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedSubmission(submission);
                        setStatus(submission.status);
                        setNotes(submission.notes || '');
                      }}
                      className="border-[#38BDF8]/30 text-white hover:bg-white/10"
                    >
                      <LucideIcons.Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteSubmission(submission.id)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <LucideIcons.Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-gray-300 mb-1">Service Required:</p>
                  <p className="text-white">{submission.service}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-300 mb-1">Description:</p>
                  <p className="text-white">{submission.description}</p>
                </div>
                {submission.notes && (
                  <div>
                    <p className="text-sm font-semibold text-gray-300 mb-1">Notes:</p>
                    <p className="text-gray-400">{submission.notes}</p>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <LucideIcons.Calendar className="h-4 w-4" />
                  {new Date(submission.timestamp).toLocaleString()}
                </div>

                {/* Edit Form */}
                {selectedSubmission?.id === submission.id && (
                  <div className="mt-4 p-4 bg-white/5 rounded-lg border border-[#38BDF8]/20">
                    <h4 className="text-white font-semibold mb-4">Update Submission</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm text-gray-300 mb-2 block">Status</label>
                        <Select value={status} onValueChange={setStatus}>
                          <SelectTrigger className="bg-white/10 border-white/20 text-white">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm text-gray-300 mb-2 block">Notes</label>
                        <Textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                          placeholder="Add notes..."
                          rows={3}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => updateSubmission(submission.id)}
                          className="bg-[#2563EB] hover:bg-[#1d4ed8]"
                        >
                          Save Changes
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setSelectedSubmission(null)}
                          className="border-white/20 text-white hover:bg-white/10"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {submissions.length === 0 && (
          <Card className="bg-gradient-to-br from-[#1a2332] to-[#0f1419] border-[#38BDF8]/20">
            <CardContent className="py-12 text-center">
              <LucideIcons.Inbox className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-300 text-lg">No submissions yet</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
